import type { TourPageThemeKey } from "./types";

const tourPageStyleRoles = {
  hero: "bg-[var(--tour-hero-bg)]",
  heroContent: "[text-shadow:0_2px_14px_rgba(0,0,0,0.68)]",
  pageShell: "bg-[var(--tour-page-bg)] text-[var(--tour-page-text)]",
  heroPill:
    "border-[var(--tour-hero-chip-border)] bg-[var(--tour-hero-chip-bg)] text-[var(--tour-hero-chip-text)] shadow-[var(--tour-shadow-hero-chip)]",
  heroPhone:
    "border-[var(--tour-hero-control-border)] bg-[var(--tour-hero-control-bg)] text-white hover:bg-[var(--tour-hero-control-hover)] focus-visible:outline-[var(--tour-page-focus)]",
  heroStat:
    "border-[var(--tour-hero-card-border)] bg-[var(--tour-hero-card-bg)] text-white shadow-[var(--tour-shadow-hero-glass)]",
  heroStatLabel: "text-[var(--tour-hero-muted)]",
  heroTitle: "text-white",
  heroBody: "text-white/85",
  heroBodyRail:
    "w-fit rounded-r-lg border-l-[5px] border-l-[var(--tour-hero-body-rail)] bg-[var(--tour-hero-body-bg)] px-[18px] py-[15px] text-[var(--tour-hero-body-text)] [box-shadow:var(--tour-hero-body-shadow)] [backdrop-filter:var(--tour-hero-body-filter)]",
  heroSubtitle: "text-white/90",
  ratingBadge:
    "border border-[var(--tour-hero-card-border)] bg-[var(--tour-hero-card-bg)] text-white shadow-[var(--tour-shadow-hero-glass)]",
  ratingIcon: "text-[var(--tour-page-accent-soft)]",
  accentText: "text-[var(--tour-page-accent)]",
  accentSoft: "text-[var(--tour-page-accent-soft)]",
  accentBg: "bg-[var(--tour-page-accent)]",
  accentBorder: "border-[var(--tour-page-accent)]",
  darkSection:
    "bg-[var(--tour-page-strong)] text-[var(--tour-page-text-on-strong)]",
  darkCard: "bg-[var(--tour-dark-card-bg)]",
  darkCardBorder: "border-[var(--tour-dark-card-border)]",
  darkCardHover:
    "transition duration-200 hover:border-[var(--tour-dark-card-border-hover)] hover:bg-[var(--tour-dark-card-bg-hover)] motion-reduce:transition-none",
  darkCardIcon: "text-[var(--tour-dark-card-icon)]",
  darkCardTitle: "text-[var(--tour-dark-card-title)]",
  darkCardText: "text-[var(--tour-dark-card-text)]",
  crossSellSection:
    "bg-[var(--tour-gallery-section-bg)] text-[var(--tour-page-text)]",
  crossSellTourCard:
    "border-[var(--tour-page-border)] bg-[var(--tour-page-surface)] shadow-[var(--tour-shadow-card)]",
  crossSellPackageCard:
    "border-[var(--tour-page-accent-soft)] bg-[var(--tour-page-surface)] shadow-[0_18px_42px_rgba(0,0,0,0.14)]",
  crossSellPackageChip:
    "border-[var(--tour-page-border)] bg-[var(--tour-cross-sell-chip-bg)]",
  galleryMosaicSection:
    "bg-[var(--tour-gallery-section-bg)] [background-image:var(--tour-gallery-section-image)] bg-cover bg-center text-[var(--tour-page-text-on-strong)]",
  galleryMosaicFrame:
    "border-[var(--tour-gallery-frame-border)] bg-[var(--tour-gallery-frame-bg)] shadow-[var(--tour-gallery-frame-shadow)]",
  galleryMosaicCopyOverlay: "[background:var(--tour-gallery-copy-overlay)]",
  galleryMosaicTileOverlay: "[background:var(--tour-gallery-tile-overlay)]",
  finalCtaOverlaySection:
    "bg-[var(--tour-final-cta-section-bg)] [background-image:var(--tour-final-cta-section-image)] bg-cover [background-position:var(--tour-final-cta-section-position)] text-[var(--tour-page-text)]",
  finalCtaOverlayCard:
    "border-[var(--tour-final-cta-card-border)] bg-[var(--tour-final-cta-card-bg)] shadow-[var(--tour-final-cta-card-shadow)] [backdrop-filter:var(--tour-final-cta-card-filter)]",
  finalCtaDivider: "[border-color:var(--tour-final-cta-divider)]",
  stopBadge:
    "border border-[var(--tour-stop-badge-border)] bg-[var(--tour-stop-badge-bg)] text-[var(--tour-stop-badge-text)] shadow-[0_10px_24px_rgba(0,0,0,0.24)]",
  stopsSection:
    "bg-[var(--tour-stops-section-bg)] [background-image:var(--tour-stops-section-image)] bg-cover bg-center text-[var(--tour-stops-section-text)]",
  stopsAccent: "text-[var(--tour-stops-section-accent)]",
  stopsTitle: "text-[var(--tour-stops-section-title)]",
  stopsLead: "text-[var(--tour-stops-section-lead)]",
  stopsCard:
    "bg-[var(--tour-stops-card-bg)] shadow-[var(--tour-stops-card-shadow)] [backdrop-filter:var(--tour-stops-card-filter)]",
  stopsCardBody:
    "bg-[var(--tour-stops-card-body-bg)] [backdrop-filter:var(--tour-stops-card-body-filter)]",
  stopsCardBorder: "border-[var(--tour-stops-card-border)]",
  stopsCardHover:
    "transition duration-200 hover:border-[var(--tour-stops-card-border-hover)] hover:bg-[var(--tour-stops-card-bg-hover)] motion-reduce:transition-none",
  stopsCardTitle: "text-[var(--tour-stops-card-text)]",
  stopsCardDescription: "text-[var(--tour-stops-card-muted)]",
  stopsTrigger:
    "text-[var(--tour-stops-trigger)] hover:text-[var(--tour-stops-trigger-hover)]",
  textOnDark: "text-[var(--tour-page-text-on-strong)]",
  mutedOnDark: "text-[var(--tour-page-muted-on-strong)]",
  subtleOnDark: "text-[var(--tour-page-subtle-on-strong)]",
  introSection:
    "bg-[var(--tour-intro-section-bg)] text-[var(--tour-page-text)]",
  lightSection:
    "bg-[var(--tour-feature-section-bg)] text-[var(--tour-page-text)]",
  whiteSection:
    "bg-[var(--tour-page-surface)] text-[var(--tour-page-text)]",
  neutralSection:
    "bg-[var(--tour-comparison-section-bg)] text-[var(--tour-page-text)]",
  cardBg: "bg-[var(--tour-page-surface)]",
  cardMutedBg: "bg-[var(--tour-page-surface-muted)]",
  cardBorder: "border-[var(--tour-page-border)]",
  cardShadow: "shadow-[var(--tour-shadow-card)]",
  cardHover:
    "transition duration-200 hover:border-[var(--tour-page-border-hover)] hover:shadow-[var(--tour-shadow-card-hover)] motion-reduce:transition-none",
  cardText: "text-[var(--tour-page-text)]",
  cardMutedText: "text-[var(--tour-page-muted)]",
  cardSubtleText: "text-[var(--tour-page-subtle)]",
  imageSurface: "bg-[var(--tour-page-surface-muted)]",
  faqSection:
    "bg-[var(--tour-faq-section-bg)] [background-image:var(--tour-faq-section-image)] bg-cover bg-center text-[var(--tour-page-text)]",
  faqDivider: "border-[var(--tour-faq-answer-border)]",
  logisticsSection:
    "bg-[var(--tour-logistics-section-bg)] [background-image:var(--tour-logistics-section-image)] bg-cover bg-center text-[var(--tour-page-text)]",
  logisticsCard:
    "border-[var(--tour-logistics-card-border)] bg-[var(--tour-logistics-card-bg)] text-[var(--tour-logistics-card-title)] shadow-[var(--tour-logistics-card-shadow)] [backdrop-filter:var(--tour-logistics-card-filter)]",
  logisticsCardHover:
    "transition duration-200 hover:border-[var(--tour-logistics-card-border-hover)] hover:bg-[var(--tour-logistics-card-bg-hover)] motion-reduce:transition-none",
  logisticsCardTitle: "text-[var(--tour-logistics-card-title)]",
  logisticsCardText: "text-[var(--tour-logistics-card-text)]",
  logisticsMapFrame:
    "border-[var(--tour-logistics-map-border)] bg-[var(--tour-logistics-map-bg)] shadow-[var(--tour-logistics-map-shadow)]",
  logisticsRail:
    "border-[var(--tour-logistics-rail-border)] bg-[var(--tour-logistics-rail-bg)] shadow-[var(--tour-logistics-card-shadow)]",
  logisticsRailDivider: "border-[var(--tour-logistics-rail-divider)]",
  tableRow:
    "border-b border-[var(--tour-page-border-soft)] hover:bg-[var(--tour-page-surface-muted)]",
  comparisonHighlight:
    "bg-[var(--tour-comparison-highlight-bg)] text-[var(--tour-page-text)]",
  comparisonCardLight:
    "border-[var(--tour-comparison-card-border)] bg-[var(--tour-comparison-card-bg)] text-[var(--tour-page-text)] shadow-[var(--tour-shadow-card)]",
  comparisonCardDark:
    "border-[var(--tour-page-strong-border)] bg-[var(--tour-page-strong)] text-[var(--tour-page-text-on-strong)] shadow-[var(--tour-shadow-card)]",
  comparisonIconLight: "text-[var(--tour-page-accent)]",
  comparisonIconDark: "text-[var(--tour-page-accent-soft)]",
  comparisonRecommendation:
    "border-[var(--tour-page-accent-soft)] bg-[var(--tour-page-surface)] text-[var(--tour-page-text)]",
  comparisonCta:
    "bg-[var(--tour-page-button)] text-[var(--tour-page-button-text)] hover:bg-[var(--tour-page-button-hover)] focus-visible:outline-[var(--tour-page-focus)]",
  historySection:
    "bg-[var(--tour-history-section-bg)] [background-image:var(--tour-history-section-image)] bg-cover bg-center text-[var(--tour-page-text)]",
  historyTimelineLine: "bg-[var(--tour-history-line-bg)]",
  historyTimelineCard:
    "border-[var(--tour-history-card-border)] bg-[var(--tour-history-card-bg)] text-[var(--tour-history-title)] shadow-[var(--tour-history-card-shadow)]",
  historyTimelineMarker:
    "bg-[var(--tour-history-marker-bg)] text-[var(--tour-history-marker-text)] ring-[var(--tour-history-marker-ring)]",
  historyTimelineYear: "text-[var(--tour-history-marker-bg)]",
  historyTimelineTitle: "text-[var(--tour-history-title)]",
  historyTimelineText: "text-[var(--tour-history-text)]",
  linkPanel:
    "hover:border-[var(--tour-page-accent)] hover:bg-[var(--tour-page-surface-muted)]",
  focus: "focus-visible:outline-[var(--tour-page-focus)]",
  link:
    "text-[var(--tour-page-link)] hover:text-[var(--tour-page-link-hover)]",
  button:
    "bg-[var(--tour-page-button)] text-[var(--tour-page-button-text)] hover:bg-[var(--tour-page-button-hover)] focus-visible:outline-[var(--tour-page-focus)]",
  accentFocus: "focus-visible:outline-[var(--tour-page-accent-soft)]",
  faqIcon: "bg-[var(--tour-faq-icon-bg)] text-[var(--tour-faq-icon)]",
  faqItem:
    "border-[var(--tour-faq-item-border)] bg-[var(--tour-faq-item-bg)] text-[var(--tour-page-text)] shadow-[var(--tour-faq-item-shadow)] transition-colors hover:border-[var(--tour-page-border-hover)] open:bg-[var(--tour-faq-item-bg-open)] motion-reduce:transition-none",
  faqTrigger: "text-[var(--tour-faq-question)]",
  faqAnswer: "text-[var(--tour-faq-answer)]",
  reviewAvatar:
    "bg-[var(--tour-review-avatar-bg)] text-white shadow-[var(--tour-shadow-avatar)]",
  reviewsSection:
    "bg-[var(--tour-reviews-section-bg)] text-[var(--tour-page-text)]",
  reviewCard:
    "rounded-lg border border-[var(--tour-page-border)] bg-[var(--tour-page-surface)] text-[var(--tour-page-text)] shadow-[var(--tour-shadow-card)]",
  reviewBadge:
    "border-[var(--tour-page-border)] bg-[var(--tour-page-surface-muted)]",
  reviewQuote:
    "h-auto min-h-[150px] max-h-none overflow-visible pr-0 text-[var(--tour-page-muted)]",
  reviewDate: "text-[var(--tour-page-subtle)]",
  sectionTitle: "text-[var(--tour-page-text)]",
  sectionLead: "text-[var(--tour-page-muted)]",
  narrativeSection:
    "bg-[var(--tour-narrative-section-bg)] text-[var(--tour-page-text)]",
  narrativePanel:
    "border-[var(--tour-page-border)] bg-[var(--tour-page-surface-muted)] text-[var(--tour-page-text)]",
  mapPlaceholder: "bg-[var(--tour-page-surface-muted)]",
} as const;

export const tourPageThemeStyles = {
  garden: tourPageStyleRoles,
  quarter: tourPageStyleRoles,
  ghostBus: tourPageStyleRoles,
} satisfies Record<TourPageThemeKey, typeof tourPageStyleRoles>;

const anchorNavStyleRoles = {
  navShell:
    "border-[var(--tour-nav-border)] bg-[var(--tour-nav-bg)] text-[var(--tour-page-text)]",
  navActive:
    "border-[var(--tour-page-accent)] bg-[var(--tour-nav-active-bg)] text-[var(--tour-nav-active-text)] shadow-[var(--tour-nav-active-shadow)]",
  navInactive:
    "border-transparent text-[var(--tour-nav-inactive-text)] hover:bg-[var(--tour-nav-hover-bg)] hover:text-[var(--tour-nav-hover-text)]",
  navFocus:
    "focus-visible:outline-[var(--tour-page-focus)] focus-visible:bg-[var(--tour-nav-hover-bg)]",
} as const;

export const tourPageAnchorNavThemeStyles = {
  garden: anchorNavStyleRoles,
  quarter: anchorNavStyleRoles,
  ghostBus: anchorNavStyleRoles,
} satisfies Record<TourPageThemeKey, typeof anchorNavStyleRoles>;

const stickyCtaStyleRoles = {
  stickyBg: "bg-[var(--tour-sticky-bg)]",
  stickyBorder: "border-[var(--tour-sticky-border)]",
  meta: "text-[var(--tour-sticky-muted)]",
  button:
    "bg-[var(--tour-page-button)] text-[var(--tour-page-button-text)] hover:bg-[var(--tour-page-button-hover)] focus-visible:outline-[var(--tour-page-focus)]",
} as const;

export const tourPageStickyCtaThemeStyles = {
  garden: stickyCtaStyleRoles,
  quarter: stickyCtaStyleRoles,
  ghostBus: stickyCtaStyleRoles,
} satisfies Record<TourPageThemeKey, typeof stickyCtaStyleRoles>;
